<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="file-grid-container" v-if="favoriteFiles.length > 0">
    <button
      v-for="file in favoriteFiles"
      :key="file.id"
      class="file-card"
      @click="navigateTo(file)"
      :aria-label="file.title || file.name"
    >
      <div class="file-card-row">
        <i
          :class="getIconClass(file.mimeType)"
          class="file-icon"
          aria-hidden="true"
        ></i>
        <div class="file-info">
          <div
            class="file-name"
            v-tooltip.bottom="file.description || file.title || file.name"
          >
            {{ file.title || file.name }}
          </div>
          <div v-if="file.title" class="file-secondary-name">
            {{ file.name }}
          </div>
        </div>
      </div>
    </button>
  </div>
  <div v-else>{{ $t("emptyFavoriteFile") }}</div>
</template>

<script>
import { useCollaboraStore } from "@/stores/collabora";
import { useConfigStore } from "@/stores/config";
import { useFavoritesStore } from "@/stores/favorites";
import { mapState } from "pinia";
import Tooltip from "primevue/tooltip";

export default {
  name: "FavoriteFileList",
  directives: {
    tooltip: Tooltip,
  },
  props: {
    siteId: {
      type: String,
      default: "",
    },
    urlResolver: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      favorites: [],
    };
  },
  computed: {
    ...mapState(useConfigStore, ["ESPACES_HOST"]),
    favoriteFiles() {
      return this.favorites
        .map((fav) => fav.target.file)
        .filter((file) => file !== undefined && file !== null)
        .filter((file) => this.matchesSite(file))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    const favoritesStore = useFavoritesStore();
    favoritesStore
      .loadFavorites()
      .then(() => {
        this.favorites = favoritesStore.fileFavorites;
      })
      .catch((err) => {
        console.error("Error loading favorites:", err);
      });
  },
  methods: {
    matchesSite(node) {
      if (!this.siteId) {
        return true;
      }
      const siteElement = node.path?.elements?.find(
        (el) => el.nodeType === "st:site",
      );
      return siteElement?.name === this.siteId;
    },
    getIconClass(mimeType) {
      if (!mimeType) {
        return "ri-file-line";
      }
      if (mimeType === "application/pdf") {
        return "ri-file-pdf-line";
      }
      if (
        mimeType.includes("word") ||
        mimeType.includes("opendocument.text") ||
        mimeType === "application/rtf"
      ) {
        return "ri-file-word-line";
      }
      if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) {
        return "ri-file-excel-line";
      }
      if (
        mimeType.includes("powerpoint") ||
        mimeType.includes("presentation")
      ) {
        return "ri-file-ppt-line";
      }
      if (mimeType.startsWith("image/")) {
        return "ri-image-line";
      }
      if (mimeType.startsWith("video/")) {
        return "ri-video-line";
      }
      if (mimeType.startsWith("audio/")) {
        return "ri-music-line";
      }
      if (
        mimeType.includes("zip") ||
        mimeType.includes("tar") ||
        mimeType.includes("compressed")
      ) {
        return "ri-file-zip-line";
      }
      if (
        mimeType.startsWith("text/") ||
        mimeType === "application/json" ||
        mimeType === "application/xml"
      ) {
        return "ri-file-text-line";
      }
      if (mimeType === "message/rfc822") {
        return "ri-mail-line";
      }
      return "ri-file-line";
    },
    navigateTo(file) {
      const collaboraStore = useCollaboraStore();
      let destinationName = "";
      if (
        file.mimeType === "image/vnd.dxf" ||
        file.mimeType === "image/vnd.dwg" ||
        file.mimeType === "message/rfc822"
      ) {
        destinationName = "pdf";
      } else if (file.mimeType.startsWith("image/")) {
        destinationName = "image";
      } else if (file.mimeType.startsWith("video/")) {
        destinationName = "video";
      } else if (
        file.mimeType.startsWith("text/x-markdown") ||
        file.mimeType.startsWith("text/x-web-markdown")
      ) {
        destinationName = "md";
      } else if (file.mimeType.startsWith("text/html")) {
        destinationName = "html";
      } else if (file.mimeType.startsWith("text/plain")) {
        destinationName = "text";
      } else if (file.mimeType === "application/pdf") {
        destinationName = "pdf";
      } else {
        if (collaboraStore.checkIfExists(file.mimeType, "edit")) {
          destinationName = "edit";
        }
        if (collaboraStore.checkIfExists(file.mimeType, "view")) {
          destinationName = "view";
        }
        if (collaboraStore.checkIfExists(file.mimeType, "view_comment")) {
          destinationName = "view_comment";
        }
      }
      if (this.urlResolver) {
        this.urlResolver(file);
      } else {
        window.open(
          `${this.ESPACES_HOST}/espaces/${destinationName}/${file.id}`,
          "_blank",
        );
      }
    },
  },
};
</script>

<style scoped>
.file-grid-container {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.file-card {
  background-color: var(--p-content-background);
  padding: 1rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--p-content-border-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.file-card:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.file-card:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}

.file-card-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-icon {
  font-size: 1.5rem;
  color: var(--p-primary-color);
  flex-shrink: 0;
}

.file-info {
  min-width: 0;
  overflow: hidden;
}

.file-info > div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-name {
  font-weight: 500;
  font-size: 0.875rem;
}

.file-secondary-name {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-top: 0.25rem;
}
</style>
